import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePregnancyStore } from '@/store/pregnancyStore';

interface PregnancyRequiredGuardProps {
  children: React.ReactNode;
}

/**
 * PregnancyRequiredGuard - Restricts access to certain features.
 *
 * Access is granted if:
 * 1. User has an active pregnancy, OR
 * 2. User is in baby mode (after birth with premium access)
 *
 * Redirects to dashboard if user is a free user without active pregnancy (idle).
 */
export function PregnancyRequiredGuard({ children }: PregnancyRequiredGuardProps) {
  const { currentPregnancy, appMode } = usePregnancyStore();
  const navigate = useNavigate();

  const hasAccess = currentPregnancy?.is_active || appMode === 'baby' || appMode === 'pregnancy';

  useEffect(() => {
    // Redirect free users without pregnancy to dashboard
    if (appMode === 'idle') {
      navigate('/', { replace: true });
    }
  }, [appMode, navigate]);

  // If access is denied, don't render children (redirect will happen)
  if (!hasAccess && appMode === 'idle') {
    return null;
  }

  return <>{children}</>;
}
